import { jsPDF } from 'jspdf'
import { stripEmDashes } from '@/utils/typography'
import { marked } from 'marked'
import { COLORS, PAGE, CONTENT_WIDTH, type RgbColor } from './constants'
import type { CellContent } from './types'
import { tEn } from './i18n'
import { parseInertHtml } from './inertHtml'

/**
 * Base PDF generator with common functionality shared between report types
 */
export abstract class BasePdfGenerator {
  protected pdf: jsPDF
  protected currentY: number
  protected pageNumber: number
  protected t: (key: string, params?: Record<string, string | number>, plural?: number) => string

  constructor() {
    this.pdf = new jsPDF({
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait',
    })
    this.t = tEn
    this.currentY = PAGE.marginTop + PAGE.headerHeight
    this.pageNumber = 1
  }

  /**
   * Check if we need a new page and add one if necessary
   */
  protected checkPageBreak(requiredHeight: number): void {
    const maxY = PAGE.height - PAGE.marginBottom - PAGE.footerHeight
    if (this.currentY + requiredHeight > maxY) {
      this.addNewPage()
    }
  }

  /**
   * Add a new page
   */
  protected addNewPage(): void {
    const nextPage = this.pageNumber + 1
    const totalPages = this.pdf.getNumberOfPages()

    if (nextPage <= totalPages) {
      this.pdf.setPage(nextPage)
    } else {
      this.pdf.addPage()
    }

    this.pageNumber = nextPage
    this.currentY = PAGE.marginTop + PAGE.headerHeight
  }

  /**
   * Force a page break
   */
  protected forcePageBreak(): void {
    this.addNewPage()
  }

  /**
   * Add a bookmark (signet) to the PDF outline
   */
  protected addBookmark(title: string, parent: unknown = null): unknown {
    try {
      // Vérifier si l'API outline est disponible
      if (this.pdf.outline && typeof this.pdf.outline.add === 'function') {
        return this.pdf.outline.add(parent, title, { pageNumber: this.pageNumber })
      }
    } catch (error) {
      // Si l'API outline n'est pas disponible, ignorer silencieusement
      console.warn('jsPDF outline API not available:', error)
    }
    return null
  }

  /**
   * Word-wrap text manually using getTextWidth().
   * Avoids jsPDF splitTextToSize() which corrupts accented characters.
   */
  protected wrapText(text: string, maxWidth: number): string[] {
    const words = text.split(/\s+/)
    const lines: string[] = []
    let currentLine = ''

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word
      if (this.pdf.getTextWidth(testLine) > maxWidth && currentLine) {
        lines.push(currentLine)
        currentLine = word
      } else {
        currentLine = testLine
      }
    }
    if (currentLine) {
      lines.push(currentLine)
    }
    return lines
  }

  /**
   * Set text color
   */
  protected setColor(color: RgbColor): void {
    this.pdf.setTextColor(color.r, color.g, color.b)
  }

  /**
   * Set draw color
   */
  protected setDrawColor(color: RgbColor): void {
    this.pdf.setDrawColor(color.r, color.g, color.b)
  }

  /**
   * Set fill color
   */
  protected setFillColor(color: RgbColor): void {
    this.pdf.setFillColor(color.r, color.g, color.b)
  }

  /**
   * Draw a stroke box that may span multiple pages
   */
  protected drawMultiPageStrokeBox(startPage: number, endPage: number, startY: number, endY: number): void {
    this.setDrawColor(COLORS.border)
    this.pdf.setLineWidth(0.5)

    if (startPage === endPage) {
      const boxHeight = endY - startY
      this.pdf.rect(PAGE.marginLeft, startY, CONTENT_WIDTH, boxHeight, 'S')
    } else {
      for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
        this.pdf.setPage(pageNum)

        const pageTop = pageNum === startPage ? startY : PAGE.marginTop + PAGE.headerHeight
        const pageBottom = pageNum === endPage ? endY : PAGE.height - PAGE.marginBottom

        this.pdf.line(PAGE.marginLeft, pageTop, PAGE.marginLeft, pageBottom)
        this.pdf.line(PAGE.width - PAGE.marginRight, pageTop, PAGE.width - PAGE.marginRight, pageBottom)

        if (pageNum === startPage) {
          this.pdf.line(PAGE.marginLeft, startY, PAGE.width - PAGE.marginRight, startY)
        }

        if (pageNum === endPage) {
          this.pdf.line(PAGE.marginLeft, endY, PAGE.width - PAGE.marginRight, endY)
        }
      }
    }

    this.pdf.setPage(endPage)
  }

  /**
   * Add a section title (h2 style)
   */
  protected addSectionTitle(title: string, createBookmark: boolean = false): unknown {
    this.checkPageBreak(18)
    this.currentY += 8
    
    this.pdf.setFontSize(15)
    this.pdf.setFont('helvetica', 'bold')
    this.setColor(COLORS.text)
    this.pdf.text(title, PAGE.marginLeft, this.currentY)
    
    this.currentY += 12

    // Créer un signet si demandé
    if (createBookmark) {
      return this.addBookmark(title)
    }
    return null
  }

  /**
   * Add a subsection title (h3 style)
   */
  protected addSubsectionTitle(title: string, createBookmark: boolean = false, parentBookmark: unknown = null): unknown {
    this.checkPageBreak(14)
    this.currentY += 6
    
    this.pdf.setFontSize(12)
    this.pdf.setFont('helvetica', 'bold')
    this.setColor(COLORS.text)
    this.pdf.text(title, PAGE.marginLeft, this.currentY)
    
    this.currentY += 6

    // Créer un signet si demandé
    if (createBookmark) {
      return this.addBookmark(title, parentBookmark)
    }
    return null
  }

  /**
   * Add a table row (label: value)
   */
  protected addTableRow(label: string, value: string, isEmpty: boolean = false, isFirstRow: boolean = false): void {
    const padding = 4
    const topPadding = isFirstRow ? 6 : 5
    const bottomPadding = 0
    const fontSize = 9
    const lineHeight = 3.6
    const lineSpacing = 0.4
    const separatorLineWidth = 0.5
    
    const labelWidth = CONTENT_WIDTH * 0.35
    const valueWidth = CONTENT_WIDTH * 0.65
    
    this.pdf.setFontSize(fontSize)
    this.pdf.setFont('helvetica', 'bold')
    const labelLines = this.wrapText(label, labelWidth - (padding * 2))
    
    this.pdf.setFont('helvetica', 'normal')
    const valueLines = this.wrapText(value, valueWidth - (padding * 2))
    
    const maxLines = Math.max(labelLines.length, valueLines.length)
    const contentHeight = maxLines > 1 
      ? (maxLines * lineHeight) + ((maxLines - 1) * lineSpacing)
      : (maxLines * lineHeight)
    const rowHeight = topPadding + contentHeight + bottomPadding
    const totalRowHeight = rowHeight + separatorLineWidth
    
    this.checkPageBreak(totalRowHeight)

    const startY = this.currentY

    this.setFillColor(COLORS.background)
    const backgroundHeight = rowHeight + separatorLineWidth
    this.pdf.rect(PAGE.marginLeft, startY, labelWidth, backgroundHeight, 'F')

    this.pdf.setFontSize(fontSize)
    this.pdf.setFont('helvetica', 'bold')
    this.setColor(COLORS.text)
    let labelY: number
    if (maxLines === 1) {
      labelY = startY + topPadding + (rowHeight - topPadding - bottomPadding) / 2 - (lineHeight / 2)
    } else {
      labelY = startY + topPadding
    }
    
    for (let i = 0; i < labelLines.length; i++) {
      this.pdf.text(labelLines[i], PAGE.marginLeft + padding, labelY)
      if (i < labelLines.length - 1) {
        labelY += lineHeight + lineSpacing
      } else {
        labelY += lineHeight
      }
    }

    this.pdf.setFontSize(fontSize)
    this.pdf.setFont('helvetica', 'normal')
    if (isEmpty) {
      this.setColor(COLORS.lightGray)
      this.pdf.setFont('helvetica', 'italic')
    } else {
      this.setColor(COLORS.text)
    }
    
    let valueY: number
    if (maxLines === 1) {
      valueY = startY + topPadding + (rowHeight - topPadding - bottomPadding) / 2 - (lineHeight / 2)
    } else {
      valueY = startY + topPadding
    }
    
    for (let i = 0; i < valueLines.length; i++) {
      this.pdf.text(valueLines[i], PAGE.marginLeft + labelWidth + padding, valueY)
      if (i < valueLines.length - 1) {
        valueY += lineHeight + lineSpacing
      } else {
        valueY += lineHeight
      }
    }

    const borderY = startY + rowHeight
    
    this.setDrawColor(COLORS.border)
    this.pdf.setLineWidth(separatorLineWidth)
    this.pdf.line(PAGE.marginLeft, borderY, PAGE.width - PAGE.marginRight, borderY)
    
    this.currentY = borderY + separatorLineWidth
  }

  // ---------------------------------------------------------------------------
  // Markdown-to-PDF rendering (shared across all generators)
  // ---------------------------------------------------------------------------

  /**
   * Add markdown content converted to PDF using marked.
   * Parses markdown → HTML → recursive render with proper styling.
   */
  protected addMarkdownContent(
    markdown: string | null | undefined,
    x: number,
    maxWidth: number,
    fontSize: number = 10,
    color: RgbColor = COLORS.gray
  ): void {
    if (!markdown || !markdown.trim()) return

    const html = marked(stripEmDashes(markdown)) as string
    this.renderHtmlElement(parseInertHtml(html), x, maxWidth, fontSize, color, false, false)
  }

  /**
   * Recursively render HTML elements with proper styling
   */
  protected renderHtmlElement(
    element: Element | DocumentFragment,
    x: number,
    maxWidth: number,
    fontSize: number,
    color: RgbColor,
    isBold: boolean,
    isItalic: boolean
  ): void {
    const tagName = element.nodeName.toLowerCase()

    if (tagName === 'p') {
      this.currentY += 0.5
      this.renderBlockChildren(element, x, maxWidth, fontSize, color, false, false)
      this.currentY += 0.5
      return
    }

    if (tagName === 'h1' || tagName === 'h2' || tagName === 'h3' || tagName === 'h4' || tagName === 'h5' || tagName === 'h6') {
      const level = parseInt(tagName.charAt(1))
      const headingSize = Math.max(fontSize, fontSize + (7 - level) * 1.5)
      this.currentY += 3
      this.pdf.setFontSize(headingSize)
      this.pdf.setFont('helvetica', 'bold')
      this.setColor(color)
      const text = this.extractTextContent(element)
      if (text) {
        const lines = this.wrapText(text, maxWidth)
        const lineHeight = headingSize * 0.4
        for (const line of lines) {
          this.checkPageBreak(lineHeight)
          this.pdf.text(line, x, this.currentY)
          this.currentY += lineHeight
        }
      }
      this.currentY += 2
      return
    }

    if (tagName === 'ul' || tagName === 'ol') {
      this.currentY += 0.5
      this.renderBlockChildren(element, x, maxWidth, fontSize, color, false, false)
      this.currentY += 0.5
      return
    }

    if (tagName === 'li') {
      this.pdf.setFontSize(fontSize)
      this.pdf.setFont('helvetica', 'normal')
      this.setColor(color)

      // An ORDERED list keeps its numbers (MYAC-1162). Every `li` used to draw a
      // bullet whatever its parent, so `1. 2. 3.` came out as three bullets — which
      // in an audit document is not cosmetic: prose that says "finding 2" no longer
      // points at anything. `start` is honoured because markdown can begin a list
      // anywhere, and the indent follows the marker's real width so "10." does not
      // run into its own text.
      const listMarker = this.listMarkerFor(element)
      const indent = Math.max(5, this.pdf.getTextWidth(listMarker) + 1)

      this.pdf.text(listMarker, x, this.currentY)

      this.renderBlockChildren(element, x + indent, maxWidth - indent, fontSize, color, false, false)
      this.currentY += 0.5
      return
    }

    if (tagName === 'table') {
      this.renderTable(element as HTMLTableElement, x, maxWidth, fontSize, color)
      return
    }

    if (tagName === 'blockquote') {
      this.currentY += 2
      this.setColor(COLORS.gray)
      this.renderBlockChildren(element, x + 3, maxWidth - 3, fontSize, COLORS.gray, false, false)
      this.currentY += 2
      return
    }

    if (tagName === 'pre') {
      this.currentY += 3
      this.setFillColor(COLORS.background)
      const text = this.extractTextContent(element)
      if (text) {
        const lines = this.wrapText(text, maxWidth - 4)
        const lineHeight = fontSize * 0.4
        const padding = 2
        const boxHeight = lines.length * lineHeight + (padding * 2)
        this.pdf.rect(x, this.currentY - padding, maxWidth, boxHeight, 'F')
        this.pdf.setFontSize(fontSize * 0.85)
        this.pdf.setFont('courier', 'normal')
        this.setColor(color)
        for (const line of lines) {
          this.checkPageBreak(lineHeight)
          this.pdf.text(line, x + padding, this.currentY)
          this.currentY += lineHeight
        }
      }
      this.currentY += 2
      return
    }

    if (tagName === 'code') {
      const text = this.extractTextContent(element)
      if (text) {
        this.pdf.setFontSize(fontSize * 0.85)
        this.pdf.setFont('courier', 'normal')
        this.setColor(color)
        const lines = this.wrapText(text, maxWidth)
        const lineHeight = fontSize * 0.35
        for (const line of lines) {
          this.checkPageBreak(lineHeight)
          this.pdf.text(line, x, this.currentY)
          this.currentY += lineHeight
        }
      }
      return
    }

    if (tagName === 'strong' || tagName === 'b') {
      this.renderInlineChildren(element, x, maxWidth, fontSize, color, true, isItalic, x)
      return
    }

    if (tagName === 'em' || tagName === 'i') {
      this.renderInlineChildren(element, x, maxWidth, fontSize, color, isBold, true, x)
      return
    }

    if (tagName === 'br') {
      this.currentY += fontSize * 0.4
      return
    }

    if (tagName === 'hr') {
      this.currentY += 6
      this.setDrawColor(COLORS.border)
      this.pdf.setLineWidth(0.5)
      this.pdf.line(x, this.currentY, x + maxWidth, this.currentY)
      this.currentY += 6
      return
    }

    this.renderBlockChildren(element, x, maxWidth, fontSize, color, isBold, isItalic)
  }

  /**
   * Render child elements inline (keeps a shared line flow).
   * Returns the current X cursor after rendering.
   */
  protected renderInlineChildren(
    element: Element | DocumentFragment,
    x: number,
    maxWidth: number,
    fontSize: number,
    color: RgbColor,
    isBold: boolean,
    isItalic: boolean,
    startX: number = x
  ): number {
    let currentX = startX
    const lineHeight = fontSize * 0.4
    const maxX = x + maxWidth

    const applyFont = (bold: boolean, italic: boolean): void => {
      this.pdf.setFontSize(fontSize)
      this.pdf.setFont('helvetica', bold ? 'bold' : italic ? 'italic' : 'normal')
      this.setColor(color)
    }

    const renderTextRun = (rawText: string, bold: boolean, italic: boolean): void => {
      if (!rawText) return
      const segments = rawText.split('\n')
      for (let s = 0; s < segments.length; s++) {
        const normalized = segments[s].replace(/\s+/g, ' ')
        if (normalized.length > 0) {
          applyFont(bold, italic)
          const parts = normalized.split(/(\s+)/).filter((part) => part.length > 0)
          for (const part of parts) {
            const isWhitespace = /^\s+$/.test(part)
            const partWidth = this.pdf.getTextWidth(part)

            if (isWhitespace) {
              if (currentX === x) {
                continue
              }
              if (currentX + partWidth > maxX) {
                this.checkPageBreak(lineHeight)
                this.currentY += lineHeight
                currentX = x
                continue
              }
              currentX += partWidth
              continue
            }

            if (currentX + partWidth > maxX) {
              this.checkPageBreak(lineHeight)
              this.currentY += lineHeight
              currentX = x
            }

            this.pdf.text(part, currentX, this.currentY)
            currentX += partWidth
          }
        }

        if (s < segments.length - 1) {
          this.checkPageBreak(lineHeight)
          this.currentY += lineHeight
          currentX = x
        }
      }
    }

    const blockTags = new Set([
      'p',
      'div',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'table',
      'pre',
      'blockquote',
      'hr',
    ])

    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent || ''
        renderTextRun(text, isBold, isItalic)
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tagName = (child as Element).tagName.toLowerCase()

        if (tagName === 'strong' || tagName === 'b') {
          currentX = this.renderInlineChildren(child as Element, x, maxWidth, fontSize, color, true, isItalic, currentX)
          continue
        }

        if (tagName === 'em' || tagName === 'i') {
          currentX = this.renderInlineChildren(child as Element, x, maxWidth, fontSize, color, isBold, true, currentX)
          continue
        }

        if (tagName === 'br') {
          this.checkPageBreak(lineHeight)
          this.currentY += lineHeight
          currentX = x
          continue
        }

        if (blockTags.has(tagName)) {
          if (currentX !== x) {
            this.checkPageBreak(lineHeight)
            this.currentY += lineHeight
            currentX = x
          }
          this.renderHtmlElement(child as Element, x, maxWidth, fontSize, color, isBold, isItalic)
          currentX = x
        } else {
          currentX = this.renderInlineChildren(child as Element, x, maxWidth, fontSize, color, isBold, isItalic, currentX)
        }
      }
    }

    return currentX
  }

  /**
   * Render children as a block (inline flow + finalize line).
   */
  protected renderBlockChildren(
    element: Element | DocumentFragment,
    x: number,
    maxWidth: number,
    fontSize: number,
    color: RgbColor,
    isBold: boolean,
    isItalic: boolean
  ): void {
    this.renderInlineChildren(element, x, maxWidth, fontSize, color, isBold, isItalic, x)
  }

  /**
   * Extract text content from element (excluding nested block elements)
   */
  protected extractTextContent(element: Element | DocumentFragment): string {
    let text = ''
    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent || ''
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tagName = (child as Element).tagName.toLowerCase()
        if (!['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'table', 'pre', 'blockquote'].includes(tagName)) {
          text += this.extractTextContent(child as Element)
        }
      }
    }
    return text.trim()
  }

  /**
   * Render cell content with support for inline styles (bold, italic).
   * Returns an array of lines, each line is an array of text segments with their styles.
   */
  protected renderCellContentWithStyles(
    cell: Element,
    maxWidth: number
  ): Array<Array<{ text: string; bold: boolean; italic: boolean }>> {
    const lines: Array<Array<{ text: string; bold: boolean; italic: boolean }>> = []
    let currentLine: Array<{ text: string; bold: boolean; italic: boolean }> = []
    let currentText = ''
    let currentBold = false
    let currentItalic = false

    const flushCurrentText = (): void => {
      if (currentText.trim()) {
        currentLine.push({ text: currentText, bold: currentBold, italic: currentItalic })
        currentText = ''
      }
    }

    const processNode = (node: Node, bold: boolean, italic: boolean): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || ''
        const segments = text.split('\n')

        for (let i = 0; i < segments.length; i++) {
          if (segments[i]) {
            if (currentBold !== bold || currentItalic !== italic) {
              flushCurrentText()
              currentBold = bold
              currentItalic = italic
            }
            currentText += segments[i]
          }

          if (i < segments.length - 1) {
            flushCurrentText()
            if (currentLine.length > 0) {
              lines.push(currentLine)
              currentLine = []
            }
          }
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element
        const tagName = element.tagName.toLowerCase()

        flushCurrentText()

        if (tagName === 'strong' || tagName === 'b') {
          for (const child of Array.from(element.childNodes)) {
            processNode(child, true, italic)
          }
        } else if (tagName === 'em' || tagName === 'i') {
          for (const child of Array.from(element.childNodes)) {
            processNode(child, bold, true)
          }
        } else if (tagName === 'br') {
          flushCurrentText()
          if (currentLine.length > 0) {
            lines.push(currentLine)
            currentLine = []
          }
        } else {
          for (const child of Array.from(element.childNodes)) {
            processNode(child, bold, italic)
          }
        }
      }
    }

    for (const child of Array.from(cell.childNodes)) {
      processNode(child, false, false)
    }

    flushCurrentText()
    if (currentLine.length > 0) {
      lines.push(currentLine)
    }

    const wrappedLines: Array<Array<{ text: string; bold: boolean; italic: boolean }>> = []

    for (const line of lines) {
      const combinedText = line.map(seg => seg.text).join('')
      const wrapped = this.wrapText(combinedText, maxWidth)

      for (let i = 0; i < wrapped.length; i++) {
        const wrappedLine = wrapped[i]
        const style = line[0] || { bold: false, italic: false }
        wrappedLines.push([{ text: wrappedLine, bold: style.bold, italic: style.italic }])
      }
    }

    return wrappedLines.length > 0 ? wrappedLines : [[{ text: '', bold: false, italic: false }]]
  }

  /**
   * Render a table row (header or data row)
   */
  protected renderMdTableRow(
    cellTexts: CellContent[],
    rowHeight: number,
    x: number,
    tableY: number,
    colWidth: number,
    maxWidth: number,
    cellPadding: number,
    borderWidth: number,
    lineHeight: number,
    fontSize: number,
    color: RgbColor,
    isHeader: boolean,
    backgroundColor?: RgbColor,
    numCols?: number
  ): void {
    const lastCellEndX = numCols ? x + numCols * colWidth : x + maxWidth
    const actualTableWidth = numCols
      ? lastCellEndX - x
      : maxWidth

    if (backgroundColor) {
      this.setFillColor(backgroundColor)
      this.pdf.rect(x, tableY, actualTableWidth, rowHeight, 'F')
    }

    this.pdf.setFontSize(fontSize)
    this.pdf.setFont('helvetica', isHeader ? 'bold' : 'normal')
    this.setColor(color)

    let cellX = x
    for (let j = 0; j < cellTexts.length; j++) {
      const cellLines = cellTexts[j]
      let cellTextY = tableY + cellPadding + lineHeight

      for (const line of cellLines) {
        if (Array.isArray(line) && line.length > 0 && typeof line[0] === 'object' && 'text' in line[0]) {
          let currentX = cellX + cellPadding
          const maxX = cellX + colWidth - cellPadding
          for (const segment of line as Array<{ text: string; bold: boolean; italic: boolean }>) {
            if (segment.text) {
              this.pdf.setFontSize(fontSize)
              this.pdf.setFont('helvetica', segment.bold ? (segment.italic ? 'bolditalic' : 'bold') : (segment.italic ? 'italic' : 'normal'))
              this.setColor(color)
              const segmentWidth = this.pdf.getTextWidth(segment.text)
              if (currentX + segmentWidth > maxX && currentX > cellX + cellPadding) {
                cellTextY += lineHeight
                currentX = cellX + cellPadding
              }
              this.pdf.text(segment.text, currentX, cellTextY)
              currentX += segmentWidth
            }
          }
        } else {
          this.pdf.setFontSize(fontSize)
          this.pdf.setFont('helvetica', isHeader ? 'bold' : 'normal')
          this.setColor(color)
          this.pdf.text(line as string, cellX + cellPadding, cellTextY)
        }
        cellTextY += lineHeight
      }

      this.setDrawColor(COLORS.border)
      this.pdf.setLineWidth(borderWidth)
      this.pdf.rect(cellX, tableY, colWidth, rowHeight, 'S')

      cellX += colWidth
    }
  }

  /**
   * Render HTML table with headers, alternating rows, and page breaks
   */
  /** `• ` in a bullet list; `1. `, `2. `… in a numbered one, honouring `start`. */
  private listMarkerFor(element: Element | DocumentFragment): string {
    const parent = 'parentElement' in element ? element.parentElement : null
    if (!parent || parent.tagName.toLowerCase() !== 'ol') return '• '
    const items = Array.from(parent.children).filter(
      (child) => child.tagName.toLowerCase() === 'li'
    )
    const start = Number(parent.getAttribute('start') ?? '1')
    const index = items.indexOf(element as Element)
    const n = Number.isFinite(start) ? start + Math.max(index, 0) : index + 1
    return `${n}. `
  }

  protected renderTable(
    table: HTMLTableElement,
    x: number,
    maxWidth: number,
    fontSize: number,
    color: RgbColor
  ): void {
    const rows = table.querySelectorAll('tr')
    if (rows.length === 0) return

    const cellPadding = 2
    const borderWidth = 0.5
    const lineHeight = fontSize * 0.4
    let tableY = this.currentY + 3

    const firstRow = rows[0]
    const cols = firstRow.querySelectorAll('th, td')
    const adjustedMaxWidth = maxWidth - borderWidth * 0.5
    const colWidth = (adjustedMaxWidth - (cols.length - 1) * borderWidth) / cols.length

    const thead = table.querySelector('thead')
    let headerRow: HTMLTableRowElement | null = null
    let headerStartIndex = 0

    if (thead) {
      const theadRows = thead.querySelectorAll('tr')
      if (theadRows.length > 0) {
        headerRow = theadRows[0] as HTMLTableRowElement
        headerStartIndex = theadRows.length
      }
    } else {
      const firstRowCells = firstRow.querySelectorAll('th, td')
      if (firstRowCells.length > 0 && firstRowCells[0].tagName.toLowerCase() === 'th') {
        headerRow = firstRow as HTMLTableRowElement
        headerStartIndex = 1
      }
    }

    const headerCellTexts: CellContent[] = []
    let headerRowHeight = lineHeight

    if (headerRow) {
      const headerCells = headerRow.querySelectorAll('th, td')
      for (let j = 0; j < headerCells.length; j++) {
        const cellText = this.extractTextContent(headerCells[j])
        const cellLines = this.wrapText(cellText, colWidth - (cellPadding * 2))
        headerCellTexts.push(cellLines)
        headerRowHeight = Math.max(headerRowHeight, cellLines.length * lineHeight)
      }
      headerRowHeight += cellPadding * 2

      this.checkPageBreak(headerRowHeight)

      this.renderMdTableRow(
        headerCellTexts,
        headerRowHeight,
        x,
        tableY,
        colWidth,
        maxWidth,
        cellPadding,
        borderWidth,
        lineHeight,
        fontSize,
        color,
        true,
        COLORS.tableHeaderBackground,
        cols.length
      )

      tableY += headerRowHeight
      this.currentY = tableY
    }

    for (let i = headerStartIndex; i < rows.length; i++) {
      const row = rows[i]
      const cells = row.querySelectorAll('th, td')

      let maxCellHeight = lineHeight
      const cellTexts: CellContent[] = []

      for (let j = 0; j < cells.length; j++) {
        const cell = cells[j]
        const styledLines = this.renderCellContentWithStyles(
          cell,
          colWidth - (cellPadding * 2)
        )
        cellTexts.push(styledLines)
        maxCellHeight = Math.max(maxCellHeight, styledLines.length * lineHeight)
      }

      const rowHeight = maxCellHeight + (cellPadding * 2)

      const pageBeforeBreak = this.pageNumber

      this.checkPageBreak(rowHeight)

      if (this.pageNumber !== pageBeforeBreak) {
        tableY = this.currentY + 3

        if (headerRow && headerCellTexts.length > 0) {
          this.renderMdTableRow(
            headerCellTexts,
            headerRowHeight,
            x,
            tableY,
            colWidth,
            maxWidth,
            cellPadding,
            borderWidth,
            lineHeight,
            fontSize,
            color,
            true,
            COLORS.tableHeaderBackground,
            cols.length
          )
          tableY += headerRowHeight
          this.currentY = tableY
        }
      }

      const dataRowIndex = i - headerStartIndex
      const backgroundColor = dataRowIndex % 2 === 1 ? COLORS.background : undefined

      this.renderMdTableRow(
        cellTexts,
        rowHeight,
        x,
        tableY,
        colWidth,
        maxWidth,
        cellPadding,
        borderWidth,
        lineHeight,
        fontSize,
        color,
        false,
        backgroundColor,
        cols.length
      )

      tableY += rowHeight
      this.currentY = tableY
    }

    this.currentY += 3
  }

  /**
   * Draw the shared AI-generated disclaimer (AI Act Art. 50(2)) at the bottom of
   * every page. Subclasses call this once from their addHeadersAndFooters() so the
   * notice — sourced from the single shared.ai_disclaimer i18n key — appears on all
   * exported PDFs with AI-authored content.
   */
  protected addAiGeneratedNotice(): void {
    const totalPages = this.pdf.getNumberOfPages()
    const notice = this.t('shared.ai_disclaimer')
    const lineHeight = 3
    const baseY = PAGE.height - PAGE.marginBottom + 9

    for (let i = 1; i <= totalPages; i++) {
      this.pdf.setPage(i)
      this.pdf.setFontSize(6.5)
      this.pdf.setFont('helvetica', 'italic')
      this.setColor(COLORS.lightGray)

      const lines = this.wrapText(notice, CONTENT_WIDTH)
      let lineY = baseY
      for (const line of lines) {
        this.pdf.text(line, PAGE.width / 2, lineY, { align: 'center' })
        lineY += lineHeight
      }
    }
  }

  /**
   * Add headers and footers to all pages - to be implemented by subclasses
   */
  protected abstract addHeadersAndFooters(): void

  /**
   * Generate the PDF - to be implemented by subclasses
   */
  abstract generate(filename: string): void
}
